
import React, { useState } from "react";
import {
    Box,
    Container,
    Grid,
    List,
    ListItemButton,
    ListItemText,
    Paper,
    Typography,
    Divider,
    TextField,
    InputAdornment,
    Chip,
} from "@mui/material";

import SearchIcon from "@mui/icons-material/Search";
import MenuBookRoundedIcon from "@mui/icons-material/MenuBookRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import ShieldRoundedIcon from "@mui/icons-material/ShieldRounded";
import parse from "html-react-parser";
import wikiData from "../../data/wikiData";

const WikiPage = () => {
    const [selectedId, setSelectedId] = useState(wikiData[0].id);
    const [search, setSearch] = useState("");

    const selected =
        wikiData.find((w) => w.id === selectedId) ||
        wikiData[0];

    const filteredData = wikiData.filter((w) =>
        w.title.toLowerCase().includes(search.toLowerCase())
    );

    return (
        <Box
            sx={{
                minHeight: "100vh",
                position: "relative",
                overflow: "hidden",
                py: { xs: 4, sm: 6, md: 9 },
                background:
                    "radial-gradient(ellipse at 15% 0%, rgba(35,197,105,0.13), transparent 35%), radial-gradient(ellipse at 100% 60%, rgba(20,130,75,0.08), transparent 35%), linear-gradient(155deg, #07120c 0%, #050b08 50%, #030705 100%)",
                color: "#fff",
                fontFamily: "'Poppins', sans-serif",

                "&:before": {
                    content: '""',
                    position: "absolute",
                    inset: 0,
                    opacity: 0.16,
                    pointerEvents: "none",
                    backgroundImage:
                        "linear-gradient(rgba(89,255,157,0.035) 1px, transparent 1px), linear-gradient(90deg, rgba(89,255,157,0.035) 1px, transparent 1px)",
                    backgroundSize: "42px 42px",
                    maskImage:
                        "linear-gradient(to bottom, black, transparent 75%)",
                },

                "&:after": {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: "10%",
                    right: "10%",
                    height: "1px",
                    background:
                        "linear-gradient(90deg, transparent, rgba(76,255,147,0.65), transparent)",
                },
            }}
        >
            {/* BACKGROUND ACCENTS */}
            <Box
                sx={{
                    position: "absolute",
                    top: "-220px",
                    right: "-200px",
                    width: 600,
                    height: 600,
                    borderRadius: "50%",
                    background:
                        "radial-gradient(circle, rgba(37,205,112,0.12), transparent 68%)",
                    filter: "blur(100px)",
                    pointerEvents: "none",
                }}
            />

            <Box
                sx={{
                    position: "absolute",
                    bottom: "-250px",
                    left: "-220px",
                    width: 650,
                    height: 650,
                    borderRadius: "50%",
                    background:
                        "radial-gradient(circle, rgba(25,125,75,0.12), transparent 70%)",
                    filter: "blur(110px)",
                    pointerEvents: "none",
                }}
            />

            <Container
                maxWidth="xl"
                sx={{
                    position: "relative",
                    zIndex: 1,
                }}
            >
                {/* HERO SECTION */}
                <Paper
                    elevation={0}
                    sx={{
                        position: "relative",
                        overflow: "hidden",
                        borderRadius: { xs: "20px", md: "28px" },
                        p: { xs: 3, sm: 4, md: 6 },
                        mb: { xs: 4, md: 5 },
                        background:
                            "linear-gradient(125deg, rgba(12,31,20,0.98), rgba(6,14,9,0.98) 65%, rgba(10,25,16,0.98))",
                        border: "1px solid rgba(77,255,146,0.16)",
                        boxShadow:
                            "0 25px 75px rgba(0,0,0,0.38), inset 0 1px 0 rgba(255,255,255,0.025)",

                        "&:before": {
                            content: '""',
                            position: "absolute",
                            top: 0,
                            left: 0,
                            width: "100%",
                            height: "3px",
                            background:
                                "linear-gradient(90deg, #167a43, #42f58d, #a4ffc5, #167a43)",
                        },
                    }}
                >
                    {/* HERO GLOW */}
                    <Box
                        sx={{
                            position: "absolute",
                            right: "-100px",
                            top: "-160px",
                            width: 440,
                            height: 440,
                            borderRadius: "50%",
                            background:
                                "radial-gradient(circle, rgba(47,230,121,0.12), transparent 68%)",
                            filter: "blur(55px)",
                            pointerEvents: "none",
                        }}
                    />

                    <Grid
                        container
                        spacing={{ xs: 4, md: 6 }}
                        alignItems="center"
                    >
                        {/* HERO INTRO */}
                        <Grid item xs={12} md={7}>
                            <Chip
                                icon={
                                    <MenuBookRoundedIcon
                                        sx={{
                                            color: "#55F59A !important",
                                        }}
                                    />
                                }
                                label="THE ADVENTURER'S KNOWLEDGE BASE"
                                sx={{
                                    mb: 3,
                                    height: 35,
                                    background: "rgba(47,230,121,0.08)",
                                    border: "1px solid rgba(77,255,146,0.19)",
                                    color: "#6BFFA8",
                                    fontWeight: 800,
                                    fontSize: "10px",
                                    letterSpacing: "1px",
                                    "& .MuiChip-label": {
                                        px: 1.5,
                                    },
                                }}
                            />

                            <Typography
                                sx={{
                                    color: "#F4FFF7",
                                    fontWeight: 900,
                                    fontSize: {
                                        xs: "38px",
                                        sm: "52px",
                                        md: "70px",
                                    },
                                    lineHeight: 1,
                                    letterSpacing: "-1.8px",
                                    mb: 2.5,
                                    textShadow:
                                        "0 8px 35px rgba(0,0,0,0.45)",
                                }}
                            >
                                Master the
                                <br />
                                <Box
                                    component="span"
                                    sx={{
                                        color: "#55F59A",
                                        textShadow:
                                            "0 0 35px rgba(66,245,141,0.17)",
                                    }}
                                >
                                    Classic Era.
                                </Box>
                            </Typography>

                            <Typography
                                sx={{
                                    color: "rgba(226,245,233,0.62)",
                                    fontSize: { xs: "13px", md: "15px" },
                                    lineHeight: 1.95,
                                    maxWidth: 600,
                                }}
                            >
                                Your field manual for the world of Critical
                                Conquer. Discover class guides, hunting
                                strategies, game commands, server rules, and
                                everything you need to prepare for your next
                                adventure.
                            </Typography>

                            <Box
                                sx={{
                                    display: "flex",
                                    flexWrap: "wrap",
                                    gap: 1,
                                    mt: 3.5,
                                }}
                            >
                                {["CLASSIC GAMEPLAY", "PLAYER GUIDES", "GAME KNOWLEDGE"].map(
                                    (label) => (
                                        <Box
                                            key={label}
                                            sx={{
                                                px: 1.5,
                                                py: 0.8,
                                                borderRadius: "7px",
                                                border: "1px solid rgba(100,255,160,0.11)",
                                                background:
                                                    "rgba(48,155,89,0.07)",
                                                color: "#8BCBA5",
                                                fontSize: "9px",
                                                fontWeight: 800,
                                                letterSpacing: "0.8px",
                                            }}
                                        >
                                            {label}
                                        </Box>
                                    )
                                )}
                            </Box>
                        </Grid>

                        {/* QUICK TIPS */}
                        <Grid item xs={12} md={5}>
                            <Box
                                sx={{
                                    position: "relative",
                                    p: { xs: 2.5, md: 3.5 },
                                    borderRadius: "20px",
                                    background:
                                        "linear-gradient(145deg, rgba(29,86,49,0.20), rgba(5,15,9,0.80))",
                                    border: "1px solid rgba(77,255,146,0.15)",
                                    overflow: "hidden",
                                }}
                            >
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1.5,
                                        mb: 3,
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: 44,
                                            height: 44,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            borderRadius: "13px",
                                            background:
                                                "rgba(55,230,121,0.11)",
                                            border: "1px solid rgba(77,255,146,0.16)",
                                        }}
                                    >
                                        <AutoAwesomeRoundedIcon
                                            sx={{
                                                color: "#55F59A",
                                                fontSize: 22,
                                            }}
                                        />
                                    </Box>

                                    <Box>
                                        <Typography
                                            sx={{
                                                color: "#F2FFF6",
                                                fontWeight: 900,
                                                fontSize: "16px",
                                            }}
                                        >
                                            Field Notes
                                        </Typography>

                                        <Typography
                                            sx={{
                                                color: "#659879",
                                                fontSize: "10px",
                                                mt: 0.4,
                                                letterSpacing: "1px",
                                            }}
                                        >
                                            A BETTER WAY TO BEGIN
                                        </Typography>
                                    </Box>
                                </Box>

                                {[
                                    "Learn your class before investing in gear.",
                                    "Team up with players for tougher hunts.",
                                    "Check announcements for server updates.",
                                    "Trade carefully and protect your account.",
                                ].map((tip, i) => (
                                    <Box
                                        key={i}
                                        sx={{
                                            display: "flex",
                                            alignItems: "flex-start",
                                            gap: 1.5,
                                            mb: i === 3 ? 0 : 1.5,
                                            p: 1.2,
                                            borderRadius: "10px",
                                            transition: "all 0.2s ease",
                                            "&:hover": {
                                                background:
                                                    "rgba(66,245,141,0.045)",
                                            },
                                        }}
                                    >
                                        <ShieldRoundedIcon
                                            sx={{
                                                color: "#55F59A",
                                                fontSize: 17,
                                                mt: 0.2,
                                                flexShrink: 0,
                                            }}
                                        />

                                        <Typography
                                            sx={{
                                                color: "rgba(218,240,225,0.70)",
                                                fontSize: "12px",
                                                lineHeight: 1.75,
                                            }}
                                        >
                                            {tip}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                        </Grid>
                    </Grid>
                </Paper>

                {/* MAIN WIKI LAYOUT */}
                <Grid
                    container
                    spacing={{ xs: 3, md: 4 }}
                    alignItems="flex-start"
                >
                    {/* TOPIC SIDEBAR */}
                    <Grid item xs={12} md={4}>
                        <TextField
                            fullWidth
                            placeholder="Find a guide or topic..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon
                                            sx={{
                                                color: "#55F59A",
                                                fontSize: 21,
                                            }}
                                        />
                                    </InputAdornment>
                                ),
                            }}
                            sx={{
                                mb: 2,
                                "& .MuiOutlinedInput-root": {
                                    background:
                                        "rgba(9,23,14,0.94)",
                                    color: "#F3FFF6",
                                    borderRadius: "14px",
                                    height: 54,
                                    "& fieldset": {
                                        borderColor:
                                            "rgba(77,255,146,0.15)",
                                    },
                                    "&:hover fieldset": {
                                        borderColor:
                                            "rgba(77,255,146,0.35)",
                                    },
                                    "&.Mui-focused fieldset": {
                                        borderColor: "#42E987",
                                    },
                                },
                                "& input": {
                                    fontSize: "13px",
                                },
                                "& input::placeholder": {
                                    color: "#6C8D77",
                                    opacity: 1,
                                },
                            }}
                        />

                        <Paper
                            elevation={0}
                            sx={{
                                borderRadius: { xs: "18px", md: "22px" },
                                background:
                                    "linear-gradient(155deg, rgba(11,28,17,0.98), rgba(5,13,8,0.98))",
                                border: "1px solid rgba(77,255,146,0.13)",
                                overflow: "hidden",
                                boxShadow: "0 18px 50px rgba(0,0,0,0.28)",
                            }}
                        >
                            {/* SIDEBAR HEADING */}
                            <Box
                                sx={{
                                    p: { xs: 2.5, md: 3 },
                                    borderBottom:
                                        "1px solid rgba(255,255,255,0.055)",
                                    background:
                                        "linear-gradient(90deg, rgba(41,150,78,0.10), transparent)",
                                }}
                            >
                                <Typography
                                    sx={{
                                        color: "#55F59A",
                                        fontSize: "10px",
                                        fontWeight: 900,
                                        letterSpacing: "2px",
                                        mb: 0.8,
                                    }}
                                >
                                    EXPLORE THE REALM
                                </Typography>

                                <Typography
                                    sx={{
                                        color: "#F3FFF6",
                                        fontWeight: 900,
                                        fontSize: "22px",
                                    }}
                                >
                                    Wiki Library
                                </Typography>

                                <Typography
                                    sx={{
                                        color: "#72927D",
                                        fontSize: "11px",
                                        mt: 0.7,
                                    }}
                                >
                                    Choose a subject to open its guide.
                                </Typography>

                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        mt: 2.5,
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            color: "#789A83",
                                            fontSize: "10px",
                                            fontWeight: 800,
                                            letterSpacing: "1px",
                                        }}
                                    >
                                        AVAILABLE TOPICS
                                    </Typography>

                                    <Chip
                                        label={filteredData.length}
                                        size="small"
                                        sx={{
                                            height: 24,
                                            minWidth: 30,
                                            background:
                                                "rgba(66,245,141,0.10)",
                                            border:
                                                "1px solid rgba(66,245,141,0.16)",
                                            color: "#7AFFAD",
                                            fontWeight: 800,
                                            fontSize: "11px",
                                        }}
                                    />
                                </Box>
                            </Box>

                            {/* TOPIC LIST */}
                            <List sx={{ p: 1.2 }}>
                                {filteredData.map((w) => (
                                    <ListItemButton
                                        key={w.id}
                                        selected={selectedId === w.id}
                                        onClick={() =>
                                            setSelectedId(w.id)
                                        }
                                        sx={{
                                            position: "relative",
                                            borderRadius: "12px",
                                            mb: 0.6,
                                            minHeight: 50,
                                            color: "#D5E9DB",
                                            border: "1px solid transparent",
                                            transition: "all 0.2s ease",

                                            "&.Mui-selected": {
                                                background:
                                                    "linear-gradient(100deg, rgba(47,201,104,0.16), rgba(30,100,56,0.05))",
                                                borderColor:
                                                    "rgba(77,255,146,0.15)",

                                                "&:before": {
                                                    content: '""',
                                                    position: "absolute",
                                                    left: 0,
                                                    top: "20%",
                                                    width: 3,
                                                    height: "60%",
                                                    borderRadius: "5px",
                                                    background: "#55F59A",
                                                    boxShadow:
                                                        "0 0 14px rgba(66,245,141,0.35)",
                                                },
                                            },

                                            "&:hover": {
                                                background:
                                                    "rgba(66,245,141,0.055)",
                                                borderColor:
                                                    "rgba(77,255,146,0.09)",
                                            },
                                        }}
                                    >
                                        <ListItemText
                                            primary={
                                                <Typography
                                                    sx={{
                                                        color:
                                                            selectedId === w.id
                                                                ? "#76FFAE"
                                                                : "#B7D0BE",
                                                        fontWeight:
                                                            selectedId === w.id
                                                                ? 800
                                                                : 600,
                                                        fontSize: "12.5px",
                                                        lineHeight: 1.6,
                                                    }}
                                                >
                                                    {w.icon}{" "}
                                                    {w.title}
                                                </Typography>
                                            }
                                        />
                                    </ListItemButton>
                                ))}

                                {filteredData.length === 0 && (
                                    <Box
                                        sx={{
                                            py: 5,
                                            px: 2,
                                            textAlign: "center",
                                        }}
                                    >
                                        <SearchIcon
                                            sx={{
                                                color: "#385A43",
                                                fontSize: 36,
                                                mb: 1,
                                            }}
                                        />

                                        <Typography
                                            sx={{
                                                color: "#789681",
                                                fontSize: "12px",
                                            }}
                                        >
                                            No matching guides found.
                                        </Typography>

                                        <Typography
                                            sx={{
                                                color: "#4F6C57",
                                                fontSize: "11px",
                                                mt: 0.7,
                                            }}
                                        >
                                            Try another keyword.
                                        </Typography>
                                    </Box>
                                )}
                            </List>
                        </Paper>
                    </Grid>

                    {/* ARTICLE CONTENT */}
                    <Grid item xs={12} md={8}>
                        <Paper
                            elevation={0}
                            sx={{
                                borderRadius: { xs: "18px", md: "24px" },
                                background:
                                    "linear-gradient(145deg, rgba(12,29,18,0.98), rgba(5,13,8,0.99))",
                                border: "1px solid rgba(77,255,146,0.14)",
                                boxShadow: "0 25px 65px rgba(0,0,0,0.35)",
                                overflow: "hidden",
                                position: "relative",

                                "&:before": {
                                    content: '""',
                                    position: "absolute",
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    height: "2px",
                                    background:
                                        "linear-gradient(90deg, transparent, #239D56, #66FFA3, #239D56, transparent)",
                                },
                            }}
                        >
                            <Box
                                sx={{
                                    p: {
                                        xs: 2.5,
                                        sm: 4,
                                        md: 5,
                                    },
                                }}
                            >
                                {/* ARTICLE HEADER */}
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1.5,
                                        mb: 2.5,
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: 45,
                                            height: 45,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            borderRadius: "13px",
                                            background:
                                                "rgba(66,245,141,0.09)",
                                            border:
                                                "1px solid rgba(77,255,146,0.15)",
                                            fontSize: "23px",
                                            flexShrink: 0,
                                        }}
                                    >
                                        {selected.icon}
                                    </Box>

                                    <Box>
                                        <Typography
                                            sx={{
                                                color: "#55F59A",
                                                fontSize: "10px",
                                                fontWeight: 900,
                                                letterSpacing: "1.7px",
                                                textTransform: "uppercase",
                                            }}
                                        >
                                            CRITICAL CONQUER FIELD GUIDE
                                        </Typography>

                                        <Typography
                                            sx={{
                                                color: "#668A72",
                                                fontSize: "11px",
                                                mt: 0.5,
                                            }}
                                        >
                                            Knowledge for every adventurer
                                        </Typography>
                                    </Box>
                                </Box>

                                {/* ARTICLE TITLE */}
                                <Typography
                                    sx={{
                                        color: "#F2FFF5",
                                        fontWeight: 900,
                                        fontSize: {
                                            xs: "28px",
                                            sm: "36px",
                                            md: "43px",
                                        },
                                        lineHeight: 1.15,
                                        letterSpacing: "-0.8px",
                                        wordBreak: "break-word",
                                    }}
                                >
                                    {selected.title}
                                </Typography>

                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                        mt: 2,
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: 38,
                                            height: 3,
                                            borderRadius: "5px",
                                            background: "#55F59A",
                                            boxShadow:
                                                "0 0 12px rgba(66,245,141,0.25)",
                                        }}
                                    />

                                    <Box
                                        sx={{
                                            width: 12,
                                            height: 3,
                                            borderRadius: "5px",
                                            background:
                                                "rgba(85,245,154,0.35)",
                                        }}
                                    />

                                    <Box
                                        sx={{
                                            width: 6,
                                            height: 3,
                                            borderRadius: "5px",
                                            background:
                                                "rgba(85,245,154,0.15)",
                                        }}
                                    />
                                </Box>

                                <Divider
                                    sx={{
                                        my: 3.5,
                                        borderColor:
                                            "rgba(255,255,255,0.065)",
                                    }}
                                />

                                {/* ARTICLE BODY */}
                                <Box
                                    sx={{
                                        color: "#BDD0C2",

                                        "& strong": {
                                            color: "#73FCA8",
                                            fontWeight: 800,
                                        },

                                        "& p": {
                                            marginTop: 0,
                                        },

                                        "& ul": {
                                            paddingLeft: "22px",
                                        },

                                        "& li": {
                                            marginBottom: "8px",
                                        },

                                        "& a": {
                                            color: "#73FCA8",
                                        },

                                        "& code": {
                                            color: "#8BFFB5",
                                            background:
                                                "rgba(66,245,141,0.08)",
                                            borderRadius: "4px",
                                            padding: "2px 5px",
                                        },
                                    }}
                                >
                                    {selected.content
                                        .split("\n")
                                        .map((line, i) => {
                                            const trimmed = line.trim();

                                            if (!trimmed)
                                                return (
                                                    <Box
                                                        key={i}
                                                        sx={{ height: 8 }}
                                                    />
                                                );

                                            const html = trimmed.replace(
                                                /\*\*(.*?)\*\*/g,
                                                "<strong>$1</strong>"
                                            );

                                            return (
                                                <Typography
                                                    key={i}
                                                    sx={{
                                                        mb: 2,
                                                        color: "#B5CBBB",
                                                        fontSize: {
                                                            xs: "13px",
                                                            md: "14px",
                                                        },
                                                        lineHeight: 1.95,
                                                        overflowWrap: "anywhere",
                                                    }}
                                                >
                                                    {parse(html)}
                                                </Typography>
                                            );
                                        })}
                                </Box>

                                {/* ARTICLE FOOTER */}
                                <Box
                                    sx={{
                                        mt: 4,
                                        pt: 2.5,
                                        borderTop:
                                            "1px solid rgba(255,255,255,0.06)",
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                    }}
                                >
                                    <ShieldRoundedIcon
                                        sx={{
                                            color: "#55F59A",
                                            fontSize: 17,
                                        }}
                                    />

                                    <Typography
                                        sx={{
                                            color: "#668A72",
                                            fontSize: "10px",
                                            lineHeight: 1.7,
                                        }}
                                    >
                                        Stay informed. Play smart. Protect the realm.
                                    </Typography>
                                </Box>
                            </Box>
                        </Paper>
                    </Grid>
                </Grid>

                {/* BOTTOM BRANDING */}
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 2,
                        mt: { xs: 5, md: 7 },
                    }}
                >
                    <Box
                        sx={{
                            width: { xs: 35, sm: 75 },
                            height: "1px",
                            background:
                                "linear-gradient(90deg, transparent, rgba(85,245,154,0.35))",
                        }}
                    />

                    <Typography
                        sx={{
                            color: "#52735C",
                            fontSize: "10px",
                            fontWeight: 800,
                            letterSpacing: "2px",
                            textTransform: "uppercase",
                            textAlign: "center",
                        }}
                    >
                        CRITICAL CONQUER • THE CLASSIC ERA
                    </Typography>

                    <Box
                        sx={{
                            width: { xs: 35, sm: 75 },
                            height: "1px",
                            background:
                                "linear-gradient(90deg, rgba(85,245,154,0.35), transparent)",
                        }}
                    />
                </Box>
            </Container>
        </Box>
    );
};

export default WikiPage;
